import {
  Dispatch,
  createContext,
  useContext,
  useMemo,
  useReducer
} from 'react';

export type User = {
  user_id: string;
  fullName: string;
  emails: string[];
  company: string;
  isManager: boolean;
  hasAccessToAutomation: boolean;
  profilePictureUrl: string;
  isAdmin: boolean;
  isBeta: boolean;
};

export type Company = {
  org_id: string;
  name: string;
  logo_url: string;
};

export type Provider = {
  provider_type: string;
  provider_subject: string;
  profile_picture_url: string;
  locale: string;
  oauth_user_registration_id: string;
};

export type GoogleCredentials = {
  access_token: string;
  refresh_token: string;
};

export type ConnectedAccount = {
  app: string; // e.g. "lemlist", "salesforce_rest_api", "hubspot"
  connectionId: string;
};

type UserState = {
  isUserLoading: boolean;
  user: User | null;
  company: Company | null;
  googleCredentials: GoogleCredentials | null;
  connectedAccounts: ConnectedAccount[];
};

// Define the initial state
const initialState: UserState = {
  isUserLoading: true,
  user: null,
  company: null,
  googleCredentials: null,
  connectedAccounts: []
};

type Action =
  | { type: 'SET_IS_USER_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: any }
  | { type: 'SET_COMPANY'; payload: Company }
  | { type: 'SET_GOOGLE_CREDENTIALS'; payload: GoogleCredentials }
  | { type: 'SET_CONNECTED_ACCOUNTS'; payload: ConnectedAccount[] };

// Define the reducer function
function reducer(state: any, action: any) {
  switch (action.type) {
    case 'SET_IS_USER_LOADING':
      return {
        ...state,
        isUserLoading: action.payload
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload
      };
    case 'SET_COMPANY':
      return {
        ...state,
        company: action.payload
      };
    case 'SET_GOOGLE_CREDENTIALS':
      return {
        ...state,
        googleCredentials: action.payload
      };
    case 'SET_CONNECTED_ACCOUNTS':
      return {
        ...state,
        connectedAccounts: action.payload
      };
    default:
      return state;
  }
}

// Create the context object
const UserContext = createContext<[UserState, Dispatch<Action>] | undefined>(
  undefined
);

// Create the provider component
function UserContextProvider({ children }: { children: React.ReactNode }) {
  const reducerValue = useReducer(reducer, initialState);
  // useReducer already returns a stable reference, so we can use it directly
  // The dispatch function is stable, and the state reference only changes when state actually changes
  return (
    <UserContext.Provider value={reducerValue}>{children}</UserContext.Provider>
  );
}

function useUserContextController() {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error(
      'userUserContext should be used inside the UserContextProvider.'
    );
  }
  return context;
}

// Define the actions
const setIsUserLoading = (dispatch: Dispatch<Action>, value: boolean) => {
  dispatch({ type: 'SET_IS_USER_LOADING', payload: value });
};
const setUser = (dispatch: Dispatch<Action>, value: any) => {
  dispatch({ type: 'SET_USER', payload: value });
};
const setCompany = (dispatch: Dispatch<Action>, value: Company) => {
  dispatch({ type: 'SET_COMPANY', payload: value });
};
const setGoogleCredentials = (
  dispatch: Dispatch<Action>,
  value: GoogleCredentials
) => {
  dispatch({ type: 'SET_GOOGLE_CREDENTIALS', payload: value });
};
const setConnectedAccounts = (
  dispatch: Dispatch<Action>,
  value: ConnectedAccount[]
) => {
  dispatch({ type: 'SET_CONNECTED_ACCOUNTS', payload: value });
};

export {
  UserContextProvider,
  useUserContextController,
  setIsUserLoading,
  setUser,
  setCompany,
  setGoogleCredentials,
  setConnectedAccounts
};
